import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, CalendarCheck } from "lucide-react";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Can you put a metal roof over my existing mobile home roof?",
    a: "Yes, we install metal roof-overs; the inspection confirms whether your roof is a good candidate.",
  },
  {
    q: "Do you do TPO roof-overs on mobile homes?",
    a: "Yes, for flat and low-slope mobile home roofs.",
  },
  {
    q: "Is a roof coating enough?",
    a: "Sometimes, if the existing roof is still sound; the inspection tells us.",
  },
  {
    q: "Can you just fix the leak?",
    a: "Yes, many leaks at seams, vents and flashing can be repaired.",
  },
  {
    q: "Do you serve my area?",
    a: "We serve mobile and manufactured homes throughout Citrus County. View all service areas.",
  },
];

const SERVICE_CARDS = [
  {
    title: "Metal roof-overs",
    description: "A new metal roof installed over the existing mobile home roof.",
    to: "/metal-roofing",
    linkLabel: "Explore metal roofing",
  },
  {
    title: "TPO and rubber roof-overs",
    description: "A membrane roof-over for flat and low-slope mobile home roofs.",
    to: "/flat-roofing",
    linkLabel: "Explore flat roofing",
  },
  {
    title: "Roof coatings",
    description: "Coatings applied to existing mobile home roofs that are still in sound condition.",
  },
  {
    title: "Leak repairs",
    description: "Finding and fixing leaks at seams, vents, flashing and other common trouble spots.",
    to: "/roof-repair",
    linkLabel: "Explore roof repair",
  },
] as const;

function TrustRow() {
  const items = [
    "Free roof inspections",
    "Family-owned roofing contractor",
    "Metal and TPO roof-overs",
    "Roof coatings and leak repairs",
    "Licensed and insured",
    `License # ${BUSINESS.licenseNumber}`,
  ];

  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Route = createFileRoute("/mobile-home-roofing")({
  head: () => ({
    meta: [
      { title: "Mobile Home Roofing in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Mobile and manufactured home roofing in Citrus County, including metal and TPO roof-overs, roof coatings and leak repairs. Free roof inspection." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Mobile Home Roofing in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Metal and TPO roof-overs, roof coatings and leak repairs for mobile and manufactured homes across Citrus County." },
      { property: "og:url", content: canonical("/mobile-home-roofing") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical("/mobile-home-roofing") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([
          { label: "Services", href: "/services" },
          { label: "Mobile Home Roofing", href: "/mobile-home-roofing" },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Mobile and Manufactured Home Roofing",
          serviceType: "Mobile Home Roofing",
          description: "Metal and TPO roof-overs, roof coatings and leak repairs for mobile and manufactured homes across Citrus County, Florida.",
          url: canonical("/mobile-home-roofing"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        },
      ]),
    }],
  }),
  component: MobileHomeRoofingPage,
});

function MobileHomeRoofingPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Mobile Home Roofing", href: "/mobile-home-roofing" }]} />
      <PageHero showRating
        eyebrow="Mobile Home Roofing"
        title="Mobile & Manufactured Home Roofing in Citrus County, FL"
        subtitle="Metal and TPO roof-overs, roof coatings and leak repairs, starting with a free roof inspection."
      />
      <ServicePageBody title="Mobile Home Roofing" service="Mobile Home Roofing">
        <p className="text-lg">
          Destination Roofing installs metal and TPO roof-overs, applies roof coatings and repairs leaks on mobile and manufactured homes across Citrus County. Every job starts with a free roof inspection so we can tell you whether a repair, coating or full roof-over makes the most sense.
        </p>

        <TrustRow />

        <h2>Mobile home roofing services we offer</h2>
        <div className="not-prose grid gap-4 sm:grid-cols-2 my-6">
          {SERVICE_CARDS.map((card) => (
            <div key={card.title} className="border border-border rounded-lg bg-card p-5">
              <h3 className="font-display font-bold text-lg mb-2">{card.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
              {"to" in card && card.to && (
                <Link to={card.to} className="inline-flex mt-3 text-sm font-semibold text-brand hover:underline">
                  {card.linkLabel} →
                </Link>
              )}
            </div>
          ))}
        </div>

        <h2>Why mobile home roofs need a different approach</h2>
        <p>
          Mobile and manufactured home roofs are often flat or low-slope and built differently from site-built homes, so the right fix depends on the roof&apos;s shape, condition and what is already on it. That is why we inspect first and explain the options with photos.
        </p>

        <h2>Common mobile home roof problems we fix</h2>
        <ul>
          <li>Leaks at seams</li>
          <li>Leaks around vents and pipes</li>
          <li>Worn or failing coatings</li>
          <li>Rust on older metal roofs</li>
          <li>Ponding water on flat sections</li>
          <li>Storm and wind damage</li>
        </ul>

        <h2>Mobile homes are a big part of Citrus County</h2>
        <p>
          About 1 in 5 homes in Citrus County is a mobile home—21.1% of housing units, compared with 8.0% statewide—according to <a href="https://www.flhealthcharts.gov/ChartsDashboards/rdPage.aspx?rdReport=NonVitalIndRateOnly.TenYrsRpt&cid=408" target="_blank" rel="noopener noreferrer">Florida Health CHARTS</a> data for 2024.
        </p>

        <h2>Insurance and mobile home roofs</h2>
        <p>
          Florida&apos;s roof-age insurance rules may apply differently to mobile home policies, so check with your agent. Read our guide to <Link to="/florida-roof-insurance-rules">Florida roof insurance and code rules</Link>.
        </p>
        <p>See our <Link to="/roof-cost-citrus-county">2026 Citrus County roof-cost guide</Link> for mobile home roof-over price ranges.</p>

        <h2>Mobile Home Roofing FAQs</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((faq) => (
            <details key={faq.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{faq.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                {faq.q === "Do you serve my area?" ? (
                  <>We serve mobile and manufactured homes throughout Citrus County. <Link to="/service-areas" className="font-semibold text-brand hover:underline">View all service areas.</Link></>
                ) : faq.a}
              </div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Schedule My Free Roof Inspection</h2>
        <p>
          We&apos;ll inspect your mobile or manufactured home roof and explain whether a repair, coating or full roof-over makes the most sense.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Roof Inspection
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}